/**
 * CraftingPanel.jsx - MapleStory-Style Crafting System
 *
 * Premium crafting interface with:
 * - Animated progress ring
 * - Two-column layout (recipes + info panel)
 * - Tier-colored accents and gradients
 * - Queue visualization with icons
 * - Rich recipe cards with rarity styling
 *
 * CSS Prefix: cp- (Crafting Panel)
 */

import React, { useState, useMemo, useEffect } from 'react';
import { useGame } from '../../context/GameContext';
import { ALL_RECIPES, CRAFTING_CONFIG, CRAFTING_CATEGORIES } from '../../data/crafting';
import { technologies } from '../../data/technologies';
import { ALL_EQUIPMENT } from '../../data/dungeon/equipment';
import {
  canAffordRecipe,
  calculateCraftTime,
  getRecipeQuality,
  formatCraftTime,
  isRecipeUnlocked,
  getInventoryMaterialCount,
} from '../../modules/crafting';
import ResourceChip from '../common/ResourceChip';
import MaterialChip from '../common/MaterialChip';
import './CraftingPanel.css';

// Tier colors matching the design system
const TIER_COLORS = {
  1: '#c4875c', // Orange-brown
  2: '#8090a0', // Gray-blue
  3: '#3ca03c', // Green
  4: '#5a9ad0', // Light blue
  5: '#a888d8', // Purple
};

/**
 * Get the actual level requirement for a recipe
 * For equipment recipes, reads from the actual equipment definition
 * For other recipes, uses the recipe's levelReq
 */
function getRecipeLevelReq(recipe) {
  // For equipment recipes, get level from actual equipment
  if (recipe.output?.type === 'equipment' && recipe.output?.itemId) {
    const equipment = ALL_EQUIPMENT[recipe.output.itemId];
    if (equipment?.levelReq !== undefined) {
      return equipment.levelReq;
    }
  }
  // Fallback to recipe's levelReq
  return recipe.levelReq || 0;
}

const CraftingPanel = () => {
  const {
    playerResources,
    playerCharacter,
    researchedTechs,
    currentCrafting,
    craftingQueue,
    craftingPending,
    startCrafting,
    cancelCrafting,
    collectCrafted,
    clearCraftingQueue,
    getCraftingProgressInfo,
  } = useGame();

  const [activeCategory, setActiveCategory] = useState('equipment');
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [progressPercent, setProgressPercent] = useState(0);

  const inventory = playerCharacter?.inventory || [];

  // Update progress animation
  useEffect(() => {
    if (!currentCrafting) {
      setProgressPercent(0);
      return;
    }

    const updateProgress = () => {
      const info = getCraftingProgressInfo();
      if (info) {
        setProgressPercent(info.percent);
      }
    };

    updateProgress();
    const interval = setInterval(updateProgress, 100);
    return () => clearInterval(interval);
  }, [currentCrafting, getCraftingProgressInfo]);

  // Get recipes for current category, sorted by tier then level requirement
  const categoryRecipes = useMemo(() => {
    return Object.values(ALL_RECIPES)
      .filter(r => r.category === activeCategory)
      .sort((a, b) => {
        // Primary sort: tier (ascending)
        if (a.tier !== b.tier) return a.tier - b.tier;
        // Secondary sort: level requirement (ascending)
        return (a.levelReq || 0) - (b.levelReq || 0);
      });
  }, [activeCategory]);

  // Recipe states
  const playerLevel = playerCharacter?.level || 1;

  // Recipe states
  const recipeStates = useMemo(() => {
    const states = {};
    categoryRecipes.forEach(recipe => {
      const unlocked = isRecipeUnlocked(recipe, researchedTechs);
      const { canAfford, missing } = canAffordRecipe(recipe, playerResources, inventory);
      const craftTime = calculateCraftTime(recipe);
      const quality = getRecipeQuality(recipe);
      // Get level requirement from actual equipment for equipment recipes
      const levelReq = getRecipeLevelReq(recipe);
      const meetsLevelReq = playerLevel >= levelReq;

      states[recipe.id] = {
        unlocked,
        canAfford,
        missing,
        craftTime,
        quality,
        levelReq,
        meetsLevelReq,
        inQueue: craftingQueue.includes(recipe.id),
        isCrafting: currentCrafting?.recipeId === recipe.id,
      };
    });
    return states;
  }, [categoryRecipes, researchedTechs, playerResources, inventory, craftingQueue, currentCrafting, playerLevel]);

  // Current crafting info
  const currentRecipe = currentCrafting ? ALL_RECIPES[currentCrafting.recipeId] : null;
  const progressInfo = currentCrafting ? getCraftingProgressInfo() : null;

  const handleCraft = (recipeId) => {
    const state = recipeStates[recipeId];
    if (!state?.unlocked || !state?.canAfford || !state?.meetsLevelReq) return;
    if (craftingQueue.length >= CRAFTING_CONFIG.maxQueueSize && !currentCrafting) return;

    startCrafting(recipeId);
  };

  const handleSelectRecipe = (recipe) => {
    setSelectedRecipe(recipe);
  };

  // Progress ring SVG
  const renderProgressRing = () => {
    const size = 120;
    const strokeWidth = 8;
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (progressPercent / 100) * circumference;

    return (
      <div className="cp-progress-ring-container">
        <svg className="cp-progress-ring" width={size} height={size}>
          <circle
            className="cp-progress-ring-bg"
            cx={size / 2}
            cy={size / 2}
            r={radius}
            strokeWidth={strokeWidth}
          />
          <circle
            className="cp-progress-ring-fill"
            cx={size / 2}
            cy={size / 2}
            r={radius}
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={offset}
          />
        </svg>
        <div className="cp-progress-ring-content">
          {currentRecipe && (
            <>
              <span className="cp-ring-icon">{currentRecipe.icon}</span>
              <span className="cp-ring-percent">{Math.round(progressPercent)}%</span>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="cp-panel">
      {/* Header with category tabs */}
      <div className="cp-header">
        <div className="cp-title">
          <span className="cp-title-icon">🔨</span>
          <span className="cp-title-text">Crafting Workshop</span>
        </div>
        <div className="cp-tabs">
          {Object.values(CRAFTING_CATEGORIES).map(cat => {
            const count = Object.values(ALL_RECIPES).filter(r => r.category === cat.id).length;
            return (
              <button
                key={cat.id}
                className={`cp-tab ${activeCategory === cat.id ? 'active' : ''}`}
                onClick={() => {
                  setActiveCategory(cat.id);
                  setSelectedRecipe(null);
                }}
              >
                <span className="cp-tab-icon">{cat.icon}</span>
                <span className="cp-tab-label">{cat.name}</span>
                <span className="cp-tab-count">{count}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Main content - two column layout */}
      <div className="cp-content">
        {/* Left: Recipe list */}
        <div className="cp-recipes-column">
          {/* Active crafting status */}
          {(currentCrafting || craftingQueue.length > 0 || craftingPending.length > 0) && (
            <div className="cp-status-bar">
              {currentCrafting && (
                <div className="cp-active-craft">
                  <div className="cp-active-icon">{currentRecipe?.icon}</div>
                  <div className="cp-active-info">
                    <span className="cp-active-name">{currentRecipe?.name}</span>
                    <div className="cp-mini-progress">
                      <div
                        className="cp-mini-progress-fill"
                        style={{ width: `${progressPercent}%` }}
                      />
                    </div>
                  </div>
                  <span className="cp-active-time">
                    {progressInfo && formatCraftTime(progressInfo.remaining)}
                  </span>
                </div>
              )}

              {craftingQueue.length > 0 && (
                <div className="cp-queue-mini">
                  <span className="cp-queue-label">Queue:</span>
                  {craftingQueue.map((id, i) => (
                    <span key={i} className="cp-queue-icon">{ALL_RECIPES[id]?.icon}</span>
                  ))}
                  <span className="cp-queue-count">{craftingQueue.length}/{CRAFTING_CONFIG.maxQueueSize}</span>
                </div>
              )}

              {craftingPending.length > 0 && (
                <div className="cp-pending-mini">
                  <span className="cp-pending-badge" onClick={() => craftingPending.forEach((_, i) => collectCrafted(0))}>
                    ✅ {craftingPending.length} Ready
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Recipe grid */}
          <div className="cp-recipes-scroll">
            <div className="cp-recipes-grid">
              {categoryRecipes.map(recipe => {
                const state = recipeStates[recipe.id];
                if (!state) return null;

                const tierColor = TIER_COLORS[recipe.tier] || TIER_COLORS[1];

                return (
                  <div
                    key={recipe.id}
                    className={`cp-recipe-card ${!state.unlocked ? 'locked' : ''} ${!state.meetsLevelReq ? 'level-locked' : ''} ${state.canAfford ? 'affordable' : ''} ${state.isCrafting ? 'crafting' : ''} ${state.inQueue ? 'queued' : ''} ${selectedRecipe?.id === recipe.id ? 'selected' : ''}`}
                    style={{ '--tier-color': tierColor }}
                    onClick={() => handleSelectRecipe(recipe)}
                  >
                    <div className="cp-recipe-tier">T{recipe.tier}</div>
                    <div className="cp-recipe-icon-wrap">
                      <span className="cp-recipe-icon">{recipe.icon}</span>
                      {state.isCrafting && <div className="cp-recipe-crafting-indicator" />}
                      {state.inQueue && <div className="cp-recipe-queue-badge">⏳</div>}
                    </div>
                    <div className="cp-recipe-name">{recipe.name}</div>
                    <div className="cp-recipe-meta">
                      {state.levelReq > 0 && (
                        <span className={`cp-recipe-level ${state.meetsLevelReq ? 'met' : 'unmet'}`}>
                          Lv{state.levelReq}
                        </span>
                      )}
                      <span className="cp-recipe-quality">{Math.round(state.quality * 100)}%</span>
                      <span className="cp-recipe-time">{formatCraftTime(state.craftTime)}</span>
                    </div>
                    {recipe.output.quantity > 1 && (
                      <div className="cp-recipe-qty">×{recipe.output.quantity}</div>
                    )}
                    {!state.unlocked && (
                      <div className="cp-recipe-lock">🔒</div>
                    )}
                    {state.unlocked && !state.meetsLevelReq && (
                      <div className="cp-recipe-level-lock">Lv{state.levelReq}</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Right: Selected recipe details / Crafting progress */}
        <div className="cp-details-column">
          {/* Ready to Collect - Always visible at top when items are pending */}
          {craftingPending.length > 0 && (
            <div className="cp-ready-section">
              <div className="cp-ready-header">
                <span className="cp-ready-icon">✨</span>
                <span className="cp-ready-title">Ready to Collect</span>
                <span className="cp-ready-count">{craftingPending.length}</span>
              </div>
              <div className="cp-ready-items">
                {craftingPending.map((pendingItem, i) => {
                  const pendingRecipe = ALL_RECIPES[pendingItem.recipeId];
                  return (
                    <button
                      key={i}
                      className="cp-ready-item"
                      onClick={() => collectCrafted(i)}
                    >
                      <span className="cp-ready-item-icon">{pendingRecipe?.icon || '📦'}</span>
                      <div className="cp-ready-item-info">
                        <span className="cp-ready-item-name">{pendingRecipe?.name || 'Unknown'}</span>
                        {pendingRecipe?.output?.quantity > 1 && (
                          <span className="cp-ready-item-qty">×{pendingRecipe.output.quantity}</span>
                        )}
                      </div>
                      <span className="cp-ready-item-action">Collect</span>
                    </button>
                  );
                })}
              </div>
              {craftingPending.length > 1 && (
                <button
                  className="cp-ready-collect-all"
                  onClick={() => {
                    // Collect all items (collect from index 0 repeatedly)
                    for (let i = 0; i < craftingPending.length; i++) {
                      collectCrafted(0);
                    }
                  }}
                >
                  Collect All ({craftingPending.length})
                </button>
              )}
            </div>
          )}

          {currentCrafting ? (
            /* Show crafting progress */
            <div className="cp-crafting-view">
              <div className="cp-crafting-header">
                <span>Currently Crafting</span>
              </div>

              {renderProgressRing()}

              <div className="cp-crafting-name">{currentRecipe?.name}</div>
              <div className="cp-crafting-desc">{currentRecipe?.description}</div>

              <button className="cp-cancel-btn" onClick={cancelCrafting}>
                Cancel (50% Refund)
              </button>

              {craftingQueue.length > 0 && (
                <div className="cp-queue-section">
                  <div className="cp-queue-header">
                    <span>Crafting Queue</span>
                    <button className="cp-clear-btn" onClick={clearCraftingQueue}>Clear</button>
                  </div>
                  <div className="cp-queue-list">
                    {craftingQueue.map((id, i) => {
                      const qRecipe = ALL_RECIPES[id];
                      return (
                        <div key={i} className="cp-queue-item">
                          <span className="cp-queue-item-icon">{qRecipe?.icon}</span>
                          <span className="cp-queue-item-name">{qRecipe?.name}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

            </div>
          ) : selectedRecipe ? (
            /* Show selected recipe details */
            <div className="cp-details-view">
              <div className="cp-details-header" style={{ '--tier-color': TIER_COLORS[selectedRecipe.tier] }}>
                <div className="cp-details-icon-wrap">
                  <span className="cp-details-icon">{selectedRecipe.icon}</span>
                </div>
                <div className="cp-details-title">
                  <span className="cp-details-name">{selectedRecipe.name}</span>
                  <span className="cp-details-tier">Tier {selectedRecipe.tier}</span>
                </div>
              </div>

              <p className="cp-details-desc">{selectedRecipe.description}</p>

              <div className="cp-details-stats">
                {recipeStates[selectedRecipe.id]?.levelReq > 0 && (
                  <div className="cp-stat-row">
                    <span className="cp-stat-label">Required Level</span>
                    <span className={`cp-stat-value cp-level-req ${recipeStates[selectedRecipe.id]?.meetsLevelReq ? 'met' : 'unmet'}`}>
                      Lv {recipeStates[selectedRecipe.id]?.levelReq} {recipeStates[selectedRecipe.id]?.meetsLevelReq ? '✓' : `(You: Lv ${playerLevel})`}
                    </span>
                  </div>
                )}
                <div className="cp-stat-row">
                  <span className="cp-stat-label">Quality</span>
                  <span className="cp-stat-value">{Math.round(recipeStates[selectedRecipe.id]?.quality * 100)}%</span>
                </div>
                <div className="cp-stat-row">
                  <span className="cp-stat-label">Craft Time</span>
                  <span className="cp-stat-value">{formatCraftTime(recipeStates[selectedRecipe.id]?.craftTime)}</span>
                </div>
                {selectedRecipe.output.quantity > 1 && (
                  <div className="cp-stat-row">
                    <span className="cp-stat-label">Output</span>
                    <span className="cp-stat-value cp-output-qty">×{selectedRecipe.output.quantity}</span>
                  </div>
                )}
              </div>

              {/* Requirements section */}
              {selectedRecipe.requiresTech && (
                <div className="cp-req-section">
                  <div className="cp-req-header">Requires Technology</div>
                  <div className={`cp-req-tech ${researchedTechs.includes(selectedRecipe.requiresTech) ? 'unlocked' : 'locked'}`}>
                    <span className="cp-req-icon">{researchedTechs.includes(selectedRecipe.requiresTech) ? '✅' : '🔒'}</span>
                    <span className="cp-req-name">{technologies[selectedRecipe.requiresTech]?.name || selectedRecipe.requiresTech}</span>
                  </div>
                </div>
              )}

              {/* Costs section */}
              <div className="cp-costs-section">
                <div className="cp-costs-header">Required Resources</div>

                {/* Kingdom resources */}
                {selectedRecipe.cost.resources && Object.keys(selectedRecipe.cost.resources).length > 0 && (
                  <div className="cp-costs-group">
                    <div className="cp-costs-label">Kingdom Resources</div>
                    <div className="cp-costs-chips">
                      {Object.entries(selectedRecipe.cost.resources).map(([resId, amount]) => (
                        <ResourceChip
                          key={resId}
                          resourceId={resId}
                          amount={amount}
                          currentAmount={playerResources[resId] || 0}
                          canAfford={(playerResources[resId] || 0) >= amount}
                          size="small"
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Dungeon materials */}
                {selectedRecipe.cost.materials && Object.keys(selectedRecipe.cost.materials).length > 0 && (
                  <div className="cp-costs-group">
                    <div className="cp-costs-label">Dungeon Materials</div>
                    <div className="cp-costs-chips">
                      {Object.entries(selectedRecipe.cost.materials).map(([matId, amount]) => {
                        const have = getInventoryMaterialCount(inventory, matId);
                        return (
                          <MaterialChip
                            key={matId}
                            materialId={matId}
                            amount={amount}
                            currentAmount={have}
                            canAfford={have >= amount}
                            size="small"
                          />
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* Craft button */}
              <button
                className={`cp-craft-btn ${!recipeStates[selectedRecipe.id]?.unlocked ? 'disabled' : ''} ${!recipeStates[selectedRecipe.id]?.meetsLevelReq ? 'disabled' : ''} ${!recipeStates[selectedRecipe.id]?.canAfford ? 'disabled' : ''}`}
                onClick={() => handleCraft(selectedRecipe.id)}
                disabled={!recipeStates[selectedRecipe.id]?.unlocked || !recipeStates[selectedRecipe.id]?.meetsLevelReq || !recipeStates[selectedRecipe.id]?.canAfford || craftingQueue.length >= CRAFTING_CONFIG.maxQueueSize}
              >
                {!recipeStates[selectedRecipe.id]?.unlocked
                  ? '🔒 Locked'
                  : !recipeStates[selectedRecipe.id]?.meetsLevelReq
                    ? `Requires Level ${recipeStates[selectedRecipe.id]?.levelReq}`
                    : !recipeStates[selectedRecipe.id]?.canAfford
                      ? 'Insufficient Resources'
                      : craftingQueue.length >= CRAFTING_CONFIG.maxQueueSize
                        ? 'Queue Full'
                        : currentCrafting
                          ? '➕ Add to Queue'
                          : '🔨 Start Crafting'
                }
              </button>
            </div>
          ) : (
            /* Empty state */
            <div className="cp-empty-details">
              <div className="cp-empty-icon">🔨</div>
              <div className="cp-empty-text">Select a recipe to view details</div>
              <div className="cp-empty-hint">
                Click on any recipe card to see costs and start crafting
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CraftingPanel;
